<template>
  <div 
    class="source-item"
    :class="{ highlighted: isHighlighted }"
    @mouseenter="handleHover"
    @mouseleave="handleUnhover"
  >
    <div class="source-info">
      <span class="source-guid">{{ guid.split('-')[0] }}...</span>
    </div>
    <button @click="toggle('stoproute')" :class="{ toggled: toggles.stoproute }" class="source-btn">
      {{ toggles.stoproute ? 'Hide Track' : 'Show Track' }}
    </button>
  </div>
</template>

<script setup>
import { defineProps, inject, onBeforeUnmount, reactive, computed } from 'vue';

const stateData = inject('stateData');

const props = defineProps({
  stopData: {
    type: Object,
    required: true
  },
  tostop: {
    type: Object,
    required: true
  },
  route: {
    type: Object,
    required: true
  },
  guid: {
    type: String,
    required: true
  },
  routeIndex: {
    type: Number,
    required: true
  },
  fromRef: {
    type: String,
    required: true
  },
  toRef: {
    type: String,
    required: true
  }
});

const isHighlighted = computed(() => {
  return stateData.hoveredRouteKey.value === `${props.fromRef}:${props.toRef}`;
});

function handleHover() {
  stateData.setExternalHighlight(props.fromRef, props.toRef);
}

function handleUnhover() {
  stateData.clearExternalHighlight(props.fromRef, props.toRef);
}

const toggles = reactive({});
let layers = {};
let layerBuilders = {};

onBeforeUnmount(() => {
  hideAll()
});

function hideAll() {
  for (let name in layers) {
    hideLayer(name);
  }
}
function hideLayer(name) {
  if (layers[name]) {
    stateData.removeLayer(layers[name]);
  }
}

async function showLayer(name) {
  if (!layers[name]) {
    if (layerBuilders[name]) {
      let fn = layerBuilders[name]
      layers[name] = await fn();
    }
  }

  if (layers[name]) {
    stateData.addLayer(layers[name]);
  }
}

function toggle(what) {
  toggles[what] = !toggles[what];
  if (!toggles[what]) {
    hideLayer(what);
  } else {
    showLayer(what);
  }
}

function layerbuilder(name, fn) {
  layerBuilders[name] = fn;
}

function popupbuilder(pos) {
  let str =
    `
    <b>idx</b>: ${pos.idx}</br> 
    <b>Dist</b>: ${pos.dist}</br>
    <b>Lat</b>: ${pos.lat}</br>
    <b>Lon</b>: ${(pos.lon)}</br>`
  return str
}

layerbuilder('stoproute', async () => {
  if (!props.fromRef || !props.toRef) {
    console.error(`Missing stop reference for route fetch: fromRef=${props.fromRef}, toRef=${props.toRef}`);
    return null;
  }

  let route = await stateData.fetchStopToStopRawRoute(props.guid, props.fromRef, props.toRef);

  if (route?.route) {
    // Map individual track points to the format expected by the builder
    const points = route.route.map(p => ({
      lat: p.gps.lat,
      lon: p.gps.lon
    })).filter(p => p.lat && p.lon);

    return stateData.buildEnhancedRouteLayer({
      points: points,
      fromRef: props.fromRef,
      toRef: props.toRef,
      baseColor: '#2196F3', // blue for individual tracks
      isDashed: true,
      refs: [props.guid]
    });
  }
});

</script>

<style scoped>
.source-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 6px;
  margin-bottom: 2px;
  border: 1px solid transparent;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.source-item:hover, .source-item.highlighted {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 193, 7, 0.3);
}

.source-guid {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.6rem;
  color: #666;
}

.source-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #888;
  font-size: 0.6rem;
  padding: 1px 4px;
  border-radius: 3px;
  cursor: pointer;
}

.source-btn.toggled {
  background: #2196F3;
  color: white;
  border-color: transparent;
}
</style>